import {
  alignReceiptLine,
  stripReceiptMarkup,
  wrapReceiptLine,
  wrapReceiptText,
} from "@/lib/receiptMarkup";
import {
  DEFAULT_RECEIPT_FOOTER_ALIGN,
  DEFAULT_RECEIPT_HEADER_ALIGN,
  DEFAULT_RECEIPT_SEPARATOR_STYLE,
  normalizeReceiptCharactersPerLine,
  type ReceiptFontFamily,
  type ReceiptFontSize,
  type ReceiptPreviewLine,
  type ReceiptPreviewModel,
  type ReceiptSeparatorStyle,
  type ReceiptTextAlignment,
} from "@/lib/receiptPreview";

export const DEFAULT_PICKUP_RECEIPT_TITLE_TEXT = "ABHOLBELEG";
export const DEFAULT_PICKUP_RECEIPT_FONT_SIZE: ReceiptFontSize = "large";
export const DEFAULT_PICKUP_RECEIPT_FONT_FAMILY: ReceiptFontFamily = "arial";

export const PICKUP_RECEIPT_PREVIEW_GUEST = "Mustermann, Max";
export const PICKUP_RECEIPT_PREVIEW_PITCH = "A12";
export const PICKUP_RECEIPT_PREVIEW_ITEMS = [
  { name: "Brötchen", quantity: "2" },
  { name: "Kaffee", quantity: "1" },
] as const;

export interface PickupReceiptPreviewOptions {
  headerText: string;
  footerText: string;
  logoConfigured: boolean;
  charactersPerLine?: number;
  separatorStyle?: ReceiptSeparatorStyle;
  headerAlign?: ReceiptTextAlignment;
  footerAlign?: ReceiptTextAlignment;
  titleText?: string;
  fontSize?: ReceiptFontSize;
  fontFamily?: ReceiptFontFamily;
  guestName?: string;
  pitchCode?: string;
  pickupDate?: string;
  items?: ReadonlyArray<{ name: string; quantity: string }>;
  now?: Date;
}

function buildSeparator(width: number, style: ReceiptSeparatorStyle): string {
  switch (style) {
    case "single":
      return "-".repeat(width);
    case "dots":
      return "·".repeat(width);
    default:
      return "=".repeat(width);
  }
}

function appendWrappedSection(
  lines: ReceiptPreviewLine[],
  text: string,
  width: number,
  alignment: ReceiptTextAlignment,
  kind: ReceiptPreviewLine["kind"],
): void {
  for (const rawLine of wrapReceiptText(text, width)) {
    for (const wrapped of wrapReceiptLine(rawLine, width)) {
      lines.push({
        text: alignReceiptLine(wrapped, width, alignment),
        align: alignment,
        kind,
      });
    }
  }
}

export function buildPickupReceiptPreviewModel(
  options: PickupReceiptPreviewOptions,
): ReceiptPreviewModel {
  const width = normalizeReceiptCharactersPerLine(options.charactersPerLine);
  const separatorStyle = options.separatorStyle ?? DEFAULT_RECEIPT_SEPARATOR_STYLE;
  const headerAlign = options.headerAlign ?? DEFAULT_RECEIPT_HEADER_ALIGN;
  const footerAlign = options.footerAlign ?? DEFAULT_RECEIPT_FOOTER_ALIGN;
  const fontSize = options.fontSize ?? DEFAULT_PICKUP_RECEIPT_FONT_SIZE;
  const fontFamily = options.fontFamily ?? DEFAULT_PICKUP_RECEIPT_FONT_FAMILY;
  const separator = buildSeparator(width, separatorStyle);
  const title = (options.titleText?.trim() || DEFAULT_PICKUP_RECEIPT_TITLE_TEXT).toUpperCase();
  const guestName = options.guestName ?? PICKUP_RECEIPT_PREVIEW_GUEST;
  const pitchCode = options.pitchCode ?? PICKUP_RECEIPT_PREVIEW_PITCH;
  const items = options.items ?? PICKUP_RECEIPT_PREVIEW_ITEMS;
  const timestamp = options.now ?? new Date();

  const lines: ReceiptPreviewLine[] = [
    { text: separator, align: "left", kind: "separator" },
  ];

  if (options.headerText.trim()) {
    appendWrappedSection(lines, options.headerText, width, headerAlign, "header");
  }

  if (options.logoConfigured) {
    lines.push({
      text: alignReceiptLine("[Logo]", width, "center"),
      align: "center",
      kind: "body",
    });
  }

  for (const wrapped of wrapReceiptLine(title, width)) {
    lines.push({
      text: alignReceiptLine(`**${wrapped}**`, width, "center"),
      align: "center",
      kind: "title",
    });
  }

  lines.push({ text: "", align: "left", kind: "body" });

  const emphasisWidth = Math.max(1, Math.floor(width / 2));
  if (options.pickupDate) {
    const pickupDate = new Date(`${options.pickupDate}T12:00:00`);
    const pickupLabel = pickupDate.toLocaleDateString("de-DE", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    for (const wrapped of wrapReceiptLine(pickupLabel, emphasisWidth)) {
      lines.push({
        text: alignReceiptLine(`##**${wrapped}**##`, width, "center"),
        align: "center",
        kind: "emphasis",
      });
    }
    lines.push({ text: "", align: "left", kind: "body" });
  }

  for (const wrapped of wrapReceiptLine(guestName, emphasisWidth)) {
    lines.push({
      text: alignReceiptLine(`##**${wrapped}**##`, width, "center"),
      align: "center",
      kind: "emphasis",
    });
  }

  const pitchLabel = `Platz ${pitchCode}`;
  for (const wrapped of wrapReceiptLine(pitchLabel, emphasisWidth)) {
    lines.push({
      text: alignReceiptLine(`##**${wrapped}**##`, width, "center"),
      align: "center",
      kind: "emphasis",
    });
  }

  lines.push({ text: separator, align: "left", kind: "separator" });
  lines.push({ text: "Artikel", align: "left", kind: "body" });

  for (const item of items) {
    const lineText = `${item.quantity}x ${item.name}`;
    for (const wrapped of wrapReceiptLine(lineText, width)) {
      lines.push({ text: wrapped, align: "left", kind: "body" });
    }
  }

  lines.push({ text: separator, align: "left", kind: "separator" });
  lines.push({
    text: alignReceiptLine(
      timestamp.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      width,
      "center",
    ),
    align: "center",
    kind: "body",
  });

  if (options.footerText.trim()) {
    appendWrappedSection(lines, options.footerText, width, footerAlign, "footer");
  }

  lines.push({ text: separator, align: "left", kind: "separator" });

  return {
    width,
    separator,
    dashLine: "-".repeat(width),
    lines,
    logoConfigured: options.logoConfigured,
    fontSize,
    fontFamily,
  };
}

export function pickupReceiptPreviewPlainText(model: ReceiptPreviewModel): string {
  return model.lines.map((line) => stripReceiptMarkup(line.text)).join("\n");
}
